// frontend/src/pages/Profile.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../api/services';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ── Icons ──────────────────────────────────────────────────────────────
function UserIcon() {
    return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
    );
}

function KeyIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
        </svg>
    );
}

function CreditCardIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
    );
}

function ShieldIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
    );
}

function LogoutIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
        </svg>
    );
}

function AdminIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
    );
}

// ── Plan Badge Colors ──────────────────────────────────────────────────
const PLAN_COLORS = {
    freemium: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
    essential: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    professional: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    expert: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    power: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const PLAN_LABELS = {
    freemium: 'Freemium',
    essential: 'Essential',
    professional: 'Professional',
    expert: 'Expert',
    power: 'Power',
};

// ── Change Password Modal ──────────────────────────────────────────────
function ChangePasswordModal({ isOpen, onClose, onSuccess, onError }) {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newPassword.length < 8) {
            onError('Password must be at least 8 characters.');
            return;
        }

        if (newPassword !== confirmPassword) {
            onError('Passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            // Route through the shared axios client: it prepends the /api base
            // path (so the request actually reaches the backend rather than the
            // SPA catch-all) and attaches the Bearer token via interceptor.
            // axios resolves only on 2xx and throws on 4xx/5xx, so reaching the
            // next line means success.
            await changePassword({
                current_password: currentPassword,
                new_password: newPassword,
            });
            onSuccess('Password changed successfully!');
            onClose();
        } catch (err) {
            // axios puts the API error body on err.response.data
            if (err?.response) {
                onError(err.response.data?.detail || 'Failed to change password.');
            } else {
                onError('Unable to reach the server. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm">
            <div className="bg-[#0d1117] border border-white/10 rounded-2xl p-6 w-full max-w-md">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-display font-bold text-white">Change Password</h3>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white transition-colors"
                    >
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
                            Current Password
                        </label>
                        <div className="relative">
                            <input
                                type={showCurrent ? 'text' : 'password'}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-brand-amber/40 focus:outline-none transition-colors"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowCurrent(!showCurrent)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                            >
                                {showCurrent ? '🙈' : '👁️'}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
                            New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showNew ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-brand-amber/40 focus:outline-none transition-colors"
                                minLength={8}
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowNew(!showNew)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                            >
                                {showNew ? '🙈' : '👁️'}
                            </button>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
                            Confirm New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirm ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-brand-amber/40 focus:outline-none transition-colors"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirm(!showConfirm)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                            >
                                {showConfirm ? '🙈' : '👁️'}
                            </button>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-white/10 text-slate-400 hover:text-white rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-2 bg-brand-amber text-black font-medium rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors"
                        >
                            {loading ? 'Changing...' : 'Change Password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── Main Profile Component ─────────────────────────────────────────────
export default function Profile() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const ADMIN_PATH = import.meta.env.VITE_ADMIN_PATH || 'gmc-secure-dashboard-7x9k2m';

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        setLoading(false);
    }, [user, navigate]);

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to logout?')) {
            logout();
            navigate('/login');
        }
    };

    const handleSuccess = (text) => {
        setMessage({ type: 'success', text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const handleError = (text) => {
        setMessage({ type: 'error', text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#07090e] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-amber border-t-transparent"></div>
            </div>
        );
    }

    if (!user) return null;

    const planColor = PLAN_COLORS[user.plan_tier] || PLAN_COLORS.freemium;
    const planLabel = PLAN_LABELS[user.plan_tier] || 'Freemium';
    const initial = user.first_name?.charAt(0)?.toUpperCase() || 'U';
    const memberSince = user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', {
        month: 'long',
        year: 'numeric',
    }) : 'Recently';

    return (
        <div className="min-h-screen bg-[#07090e] text-slate-100 font-body antialiased relative overflow-x-hidden flex flex-col selection:bg-brand-amber/30 selection:text-white">
            {/* Background Gradients */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[350px] bg-gradient-to-b from-brand-amber/5 to-transparent blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 right-[-10%] w-[500px] h-[500px] bg-indigo-500/[0.02] blur-[130px] pointer-events-none rounded-full" />

            <Navbar />

            <div className="flex-1 flex items-center justify-center px-6 py-20 md:py-28 relative z-10">
                <div className="w-full max-w-[540px]">
                    {/* Message Toast */}
                    {message.text && (
                        <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${message.type === 'success'
                            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                            : 'bg-red-500/10 border border-red-500/30 text-red-400'
                            }`}>
                            {message.text}
                        </div>
                    )}

                    {/* Profile Card */}
                    <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-8 md:p-10 backdrop-blur-sm shadow-2xl">
                        {/* Avatar & Name */}
                        <div className="flex items-center gap-5 mb-6">
                            <div className="w-16 h-16 rounded-full bg-brand-amber/20 border border-brand-amber/30 flex items-center justify-center text-2xl font-bold text-brand-amber flex-shrink-0">
                                {initial}
                            </div>
                            <div>
                                <h1 className="text-2xl font-display font-bold text-white">
                                    {user.first_name} {user.last_name || ''}
                                </h1>
                                <p className="text-slate-400 text-sm font-light">{user.email}</p>
                                <p className="text-xs text-slate-500 mt-0.5">Member since {memberSince}</p>
                            </div>
                        </div>

                        {/* Plan & Usage Section */}
                        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 mb-6">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-slate-300">Current Plan</span>
                                <span className={`px-3 py-1 text-xs font-medium rounded-full border ${planColor}`}>
                                    {planLabel}
                                </span>
                            </div>
                            <div className="space-y-1">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-400">Usage this month</span>
                                    <span className="text-white font-medium">{user.usage_count || 0} analyses</span>
                                </div>
                                <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-brand-amber rounded-full transition-all duration-500"
                                        style={{ width: `${Math.min((user.usage_count || 0) / 50 * 100, 100)}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-3">
                            {/* Change Password */}
                            <button
                                onClick={() => setShowPasswordModal(true)}
                                className="w-full flex items-center gap-3 px-4 py-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] rounded-lg transition-all duration-200"
                            >
                                <KeyIcon />
                                <span className="text-sm font-medium">Change Password</span>
                            </button>

                            {/* Manage Subscription */}
                            <button
                                onClick={() => navigate('/plans')}
                                className="w-full flex items-center gap-3 px-4 py-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] rounded-lg transition-all duration-200"
                            >
                                <CreditCardIcon />
                                <span className="text-sm font-medium">Manage Subscription</span>
                            </button>

                            {/* Admin Panel (only for superusers) */}
                            {user.is_superuser && (
                                <button
                                    onClick={() => navigate(`/${ADMIN_PATH}`)}
                                    className="w-full flex items-center gap-3 px-4 py-3 bg-brand-amber/10 hover:bg-brand-amber/20 border border-brand-amber/30 rounded-lg transition-all duration-200"
                                >
                                    <ShieldIcon />
                                    <span className="text-sm font-medium text-brand-amber">Admin Panel</span>
                                    <span className="ml-auto text-xs text-brand-amber/60">🔒</span>
                                </button>
                            )}

                            {/* Logout */}
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-4 py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg transition-all duration-200 text-red-400"
                            >
                                <LogoutIcon />
                                <span className="text-sm font-medium">Logout</span>
                            </button>
                        </div>

                        {/* Footer note */}
                        <p className="text-xs text-slate-500 text-center mt-6">
                            🔒 Your data is secure. Zero retention policy enforced.
                        </p>
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            <ChangePasswordModal
                isOpen={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                onSuccess={handleSuccess}
                onError={handleError}
            />

            <Footer />
        </div>
    );
}